import React, { useEffect, useMemo, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Download, Search, History as HistoryIcon } from "lucide-react";
import { formatDateTime, formatMeters } from "@/lib/format";

export default function History() {
  const [orders, setOrders] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fabricId, setFabricId] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    api.get("/fabrics").then(({ data }) => setFabrics(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = {};
        if (q.trim()) params.q = q.trim();
        if (fabricId && fabricId !== "all") params.fabric_id = fabricId;
        if (from) params.date_from = new Date(from).toISOString();
        if (to) {
          // include end-of-day
          const d = new Date(to);
          d.setHours(23, 59, 59, 999);
          params.date_to = d.toISOString();
        }
        const { data } = await api.get("/orders", { params });
        setOrders(data);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, fabricId, from, to]);

  const totalMeters = useMemo(
    () => orders.reduce((acc, o) => acc + (o.meters_deducted || 0), 0),
    [orders]
  );

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (fabricId && fabricId !== "all") params.set("fabric_id", fabricId);
    if (from) params.set("date_from", new Date(from).toISOString());
    if (to) {
      const d = new Date(to); d.setHours(23, 59, 59, 999);
      params.set("date_to", d.toISOString());
    }
    const token = getToken();
    // CSV endpoint is public for employees (read-only export); token attached if present
    const url = `${API}/orders/export.csv?${params.toString()}`;
    fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "urban_den_orders.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
      });
  };

  return (
    <div className="min-h-screen bg-[#FAF6EF]" data-testid="history-page">
      <Header subtitle="Order log" />
      <main className="max-w-md mx-auto px-5 pt-5 pb-32 space-y-5">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold text-brand-900 tracking-tight">
              Order history
            </h1>
            <p className="text-brand-700/70 mt-1 text-sm">
              {orders.length} order{orders.length !== 1 ? "s" : ""} · {formatMeters(totalMeters)}m deducted
            </p>
          </div>
          <Button
            onClick={exportCsv}
            disabled={orders.length === 0}
            className="h-11 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white text-sm px-4"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1.5" strokeWidth={2} />
            CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-3xl border border-brand-200 p-4 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-brand-700/50" strokeWidth={1.6} />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search order / product / fabric…"
              className="pl-11 pr-4 rounded-2xl bg-brand-50 border-brand-200 h-12"
              data-testid="history-search-input"
            />
          </div>

          <Select value={fabricId} onValueChange={setFabricId}>
            <SelectTrigger
              className="h-12 rounded-2xl bg-brand-50 border-brand-200"
              data-testid="history-fabric-select"
            >
              <SelectValue placeholder="All fabrics" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All fabrics</SelectItem>
              {fabrics.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.18em] text-brand-700/70">From</Label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="h-12 rounded-2xl bg-brand-50 border-brand-200"
                data-testid="history-from-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.18em] text-brand-700/70">To</Label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="h-12 rounded-2xl bg-brand-50 border-brand-200"
                data-testid="history-to-input"
              />
            </div>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="bg-white rounded-3xl border border-brand-200 p-6 text-sm text-brand-700/70">
            Loading…
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-3xl border border-brand-200 p-10 text-center" data-testid="history-empty">
            <div className="w-14 h-14 rounded-2xl bg-brand-100 mx-auto flex items-center justify-center mb-3">
              <HistoryIcon className="w-6 h-6 text-brand-700/60" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-xl font-semibold text-brand-900">No orders found</h3>
            <p className="text-sm text-brand-700/70 mt-1">Try clearing filters or place an order from the Order tab.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="history-list">
            {orders.map((o) => (
              <li
                key={o.id}
                className="bg-white rounded-2xl border border-brand-200 p-4"
                data-testid={`order-row-${o.id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-heading text-base font-semibold text-brand-900">
                    {o.order_no}
                  </span>
                  <span className="text-[10px] text-brand-700/60 shrink-0">
                    {formatDateTime(o.timestamp)}
                  </span>
                </div>
                <p className="text-sm text-brand-700 mt-1">
                  {o.product_name} · <span className="text-brand-700/60">{o.fabric_name}</span>
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="bg-brand-50 rounded-xl py-2">
                    <p className="text-[9px] uppercase tracking-wider text-brand-700/60">Qty</p>
                    <p className="font-heading text-sm font-semibold text-brand-900">{o.quantity}</p>
                  </div>
                  <div className="bg-rose-50 rounded-xl py-2">
                    <p className="text-[9px] uppercase tracking-wider text-rose-700/70">Used</p>
                    <p className="font-heading text-sm font-semibold text-rose-700">
                      −{formatMeters(o.meters_deducted)}m
                    </p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl py-2">
                    <p className="text-[9px] uppercase tracking-wider text-emerald-700/70">Stock after</p>
                    <p className="font-heading text-sm font-semibold text-emerald-700">
                      {formatMeters(o.stock_after)}m
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
